import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';

const CANVAS_WIDTH = 320;
const CANVAS_HEIGHT = 240;
const PLAYER_WIDTH = 20;
const PLAYER_HEIGHT = 8;
const PLAYER_Y = CANVAS_HEIGHT - 18;
const PLAYER_SPEED = 4;
const BULLET_SPEED = 5;
const ENEMY_BULLET_SPEED = 3;
const SHOOT_COOLDOWN_MS = 300;
const ENEMY_ROWS = 4;
const ENEMY_COLS = 8;
const ENEMY_WIDTH = 16;
const ENEMY_HEIGHT = 12;
const ENEMY_PADDING_X = 12;
const ENEMY_PADDING_Y = 14;
const ENEMY_OFFSET_TOP = 20;
const ENEMY_OFFSET_LEFT = (CANVAS_WIDTH - (ENEMY_COLS * (ENEMY_WIDTH + ENEMY_PADDING_X) - ENEMY_PADDING_X)) / 2;
const START_LIVES = 3;
const TICK_MS = 33;
const DIVE_CHANCE = 0.004;
const MAX_CONCURRENT_DIVES = 2;
const DIVE_SPEED = 0.012;
const RETURN_LERP = 0.05;
const SWOOP_AMPLITUDE = 40;
const ENEMY_FIRE_CHANCE = 0.02;
const HIT_RADIUS = 8;
const CRASH_RADIUS = 12;

const ROW_COLORS = ['#ffb000', '#33ff66', '#33ff66', '#1fae4a'];
const ROW_POINTS = [150, 80, 80, 50];

type EnemyState = 'formation' | 'diving' | 'returning';

interface Enemy {
  slotX: number;
  slotY: number;
  x: number;
  y: number;
  alive: boolean;
  state: EnemyState;
  diveT: number;
  phase: number;
  color: string;
  points: number;
}

interface Bullet {
  x: number;
  y: number;
  vy: number;
}

type GameStatus = 'playing' | 'won' | 'lost';

@Component({
  selector: 'app-galaga-game',
  host: {
    role: 'dialog',
    'aria-modal': 'true',
    'aria-label': 'Galaga mini-game',
    '(document:keydown)': 'onKeydown($event)',
    '(document:keyup)': 'onKeyup($event)'
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="galaga-panel">
      <div class="galaga-header">
        <span class="galaga-title">*** GALAGA.EXE ***</span>
        <span class="galaga-score">SCORE: {{ score() }}</span>
        <span class="galaga-lives">LIVES: {{ lives() }}</span>
        <button class="galaga-close" type="button" (click)="requestClose()">[ESC] CLOSE</button>
      </div>
      <canvas #canvasEl [width]="canvasWidth" [height]="canvasHeight" class="galaga-canvas"></canvas>
      @if (status() !== 'playing') {
        <div class="galaga-gameover">
          {{ status() === 'won' ? 'SQUADRON ELIMINATED!' : 'SHIP DESTROYED' }} — SCORE {{ score() }} — PRESS ENTER TO RESTART
        </div>
      } @else {
        <div class="galaga-hint">ARROWS / A,D MOVE — SPACE TO FIRE — ESC TO EXIT</div>
      }
    </div>
  `,
  styles: [`
    :host {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      background: rgba(0, 0, 0, 0.85);
      overflow: auto;
    }

    .galaga-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      padding: 16px;
      background: #010c05;
      border: 2px solid #33ff66;
      border-radius: 4px;
      box-shadow: 0 0 24px rgba(51, 255, 102, 0.45);
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
      color: #33ff66;
    }

    .galaga-header {
      display: flex;
      align-items: center;
      gap: 18px;
      width: 100%;
      justify-content: space-between;
      flex-wrap: wrap;
    }

    .galaga-title {
      font-weight: bold;
      letter-spacing: 2px;
      color: #ffb000;
      text-shadow: 0 0 6px rgba(255, 176, 0, 0.7);
    }

    .galaga-score,
    .galaga-lives {
      font-size: 0.9rem;
    }

    .galaga-close {
      background: transparent;
      border: 1px solid #33ff66;
      color: #33ff66;
      font-family: inherit;
      padding: 4px 10px;
      cursor: pointer;
      border-radius: 2px;
    }

    .galaga-close:hover {
      background: rgba(51, 255, 102, 0.15);
    }

    .galaga-canvas {
      width: auto;
      height: min(360px, calc(100vh - 180px));
      max-width: calc(100vw - 64px);
      image-rendering: pixelated;
      background: #000;
      border: 1px solid rgba(51, 255, 102, 0.5);
    }

    .galaga-gameover,
    .galaga-hint {
      font-size: 0.85rem;
      text-align: center;
      opacity: 0.9;
    }
  `]
})
export class GalagaGame implements OnInit, OnDestroy {
  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvasEl');
  private ctx: CanvasRenderingContext2D | null = null;
  private intervalId: ReturnType<typeof setInterval> | undefined;
  private readonly pressedKeys = new Set<string>();
  private lastShotAt = 0;
  private tickCount = 0;

  readonly canvasWidth = CANVAS_WIDTH;
  readonly canvasHeight = CANVAS_HEIGHT;

  readonly close = output<void>();
  readonly score = signal(0);
  readonly lives = signal(START_LIVES);
  readonly status = signal<GameStatus>('playing');

  private playerX = (CANVAS_WIDTH - PLAYER_WIDTH) / 2;
  private enemies: Enemy[] = [];
  private playerBullets: Bullet[] = [];
  private enemyBullets: Bullet[] = [];

  ngOnInit(): void {
    this.resetState();
    const canvas = this.canvasRef().nativeElement;
    this.ctx = canvas.getContext('2d');
    this.intervalId = setInterval(() => this.tick(), TICK_MS);
  }

  ngOnDestroy(): void {
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId);
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.requestClose();
      return;
    }
    if (event.key === 'Enter' && this.status() !== 'playing') {
      this.restart();
      return;
    }
    if (event.code === 'Space') {
      event.preventDefault();
      this.shoot();
      return;
    }
    const key = event.key.toLowerCase();
    if (['arrowleft', 'arrowright', 'a', 'd'].includes(key)) {
      event.preventDefault();
      this.pressedKeys.add(key);
    }
  }

  onKeyup(event: KeyboardEvent): void {
    this.pressedKeys.delete(event.key.toLowerCase());
  }

  requestClose(): void {
    this.close.emit();
  }

  private restart(): void {
    this.resetState();
  }

  private resetState(): void {
    this.playerX = (CANVAS_WIDTH - PLAYER_WIDTH) / 2;
    this.enemies = [];
    for (let row = 0; row < ENEMY_ROWS; row++) {
      for (let col = 0; col < ENEMY_COLS; col++) {
        const slotX = ENEMY_OFFSET_LEFT + col * (ENEMY_WIDTH + ENEMY_PADDING_X);
        const slotY = ENEMY_OFFSET_TOP + row * (ENEMY_HEIGHT + ENEMY_PADDING_Y);
        this.enemies.push({
          slotX,
          slotY,
          x: slotX,
          y: slotY,
          alive: true,
          state: 'formation',
          diveT: 0,
          phase: Math.random() * Math.PI * 2,
          color: ROW_COLORS[row % ROW_COLORS.length],
          points: ROW_POINTS[row % ROW_POINTS.length]
        });
      }
    }
    this.playerBullets = [];
    this.enemyBullets = [];
    this.score.set(0);
    this.lives.set(START_LIVES);
    this.status.set('playing');
    this.pressedKeys.clear();
    this.tickCount = 0;
  }

  private tick(): void {
    if (this.status() === 'playing') {
      this.tickCount++;
      this.updatePlayer();
      this.updateEnemies();
      this.enemyFire();
      this.updateBullets();
      this.checkCollisions();
      this.checkEndConditions();
    }
    this.draw();
  }

  private updatePlayer(): void {
    if (this.pressedKeys.has('arrowleft') || this.pressedKeys.has('a')) {
      this.playerX = Math.max(0, this.playerX - PLAYER_SPEED);
    }
    if (this.pressedKeys.has('arrowright') || this.pressedKeys.has('d')) {
      this.playerX = Math.min(CANVAS_WIDTH - PLAYER_WIDTH, this.playerX + PLAYER_SPEED);
    }
  }

  private updateEnemies(): void {
    const divingCount = this.enemies.filter((e) => e.alive && e.state !== 'formation').length;
    for (const enemy of this.enemies) {
      if (!enemy.alive) {
        continue;
      }
      if (enemy.state === 'formation') {
        enemy.x = enemy.slotX;
        enemy.y = enemy.slotY + Math.sin((this.tickCount + enemy.phase) * 0.05) * 2;
        if (divingCount < MAX_CONCURRENT_DIVES && Math.random() < DIVE_CHANCE) {
          enemy.state = 'diving';
          enemy.diveT = 0;
        }
      } else if (enemy.state === 'diving') {
        enemy.diveT += DIVE_SPEED;
        enemy.x = enemy.slotX + Math.sin(enemy.diveT * Math.PI * 2) * SWOOP_AMPLITUDE;
        enemy.y = enemy.slotY + enemy.diveT * (CANVAS_HEIGHT - enemy.slotY + 40);
        if (enemy.y > CANVAS_HEIGHT + 20) {
          enemy.state = 'returning';
          enemy.x = Math.random() * CANVAS_WIDTH;
          enemy.y = -20;
        }
      } else {
        enemy.x += (enemy.slotX - enemy.x) * RETURN_LERP;
        enemy.y += (enemy.slotY - enemy.y) * RETURN_LERP;
        if (Math.hypot(enemy.x - enemy.slotX, enemy.y - enemy.slotY) < 3) {
          enemy.state = 'formation';
          enemy.x = enemy.slotX;
          enemy.y = enemy.slotY;
        }
      }
    }
  }

  private enemyFire(): void {
    for (const enemy of this.enemies) {
      if (enemy.alive && enemy.state === 'diving' && Math.random() < ENEMY_FIRE_CHANCE) {
        this.enemyBullets.push({ x: enemy.x, y: enemy.y + ENEMY_HEIGHT / 2, vy: ENEMY_BULLET_SPEED });
      }
    }
  }

  private shoot(): void {
    if (this.status() !== 'playing') {
      return;
    }
    const now = Date.now();
    if (now - this.lastShotAt < SHOOT_COOLDOWN_MS) {
      return;
    }
    this.lastShotAt = now;
    this.playerBullets.push({ x: this.playerX + PLAYER_WIDTH / 2, y: PLAYER_Y, vy: -BULLET_SPEED });
  }

  private updateBullets(): void {
    this.playerBullets = this.playerBullets
      .map((bullet) => ({ ...bullet, y: bullet.y + bullet.vy }))
      .filter((bullet) => bullet.y > -10);
    this.enemyBullets = this.enemyBullets
      .map((bullet) => ({ ...bullet, y: bullet.y + bullet.vy }))
      .filter((bullet) => bullet.y < CANVAS_HEIGHT + 10);
  }

  private checkCollisions(): void {
    const spentPlayerBullets = new Set<Bullet>();
    for (const bullet of this.playerBullets) {
      for (const enemy of this.enemies) {
        if (!enemy.alive) {
          continue;
        }
        if (Math.hypot(bullet.x - (enemy.x + ENEMY_WIDTH / 2), bullet.y - (enemy.y + ENEMY_HEIGHT / 2)) < HIT_RADIUS) {
          enemy.alive = false;
          spentPlayerBullets.add(bullet);
          const bonus = enemy.state === 'formation' ? 1 : 2;
          this.score.update((s) => s + enemy.points * bonus);
          break;
        }
      }
    }
    this.playerBullets = this.playerBullets.filter((bullet) => !spentPlayerBullets.has(bullet));

    const spentEnemyBullets = new Set<Bullet>();
    for (const bullet of this.enemyBullets) {
      if (
        bullet.x >= this.playerX &&
        bullet.x <= this.playerX + PLAYER_WIDTH &&
        bullet.y >= PLAYER_Y &&
        bullet.y <= PLAYER_Y + PLAYER_HEIGHT
      ) {
        spentEnemyBullets.add(bullet);
        this.lives.update((l) => l - 1);
      }
    }
    this.enemyBullets = this.enemyBullets.filter((bullet) => !spentEnemyBullets.has(bullet));

    const playerCenterX = this.playerX + PLAYER_WIDTH / 2;
    const playerCenterY = PLAYER_Y + PLAYER_HEIGHT / 2;
    for (const enemy of this.enemies) {
      if (enemy.alive && enemy.state === 'diving' && Math.hypot(enemy.x - playerCenterX, enemy.y - playerCenterY) < CRASH_RADIUS) {
        enemy.alive = false;
        this.lives.update((l) => l - 1);
      }
    }
  }

  private checkEndConditions(): void {
    if (this.lives() <= 0) {
      this.status.set('lost');
    } else if (this.enemies.every((enemy) => !enemy.alive)) {
      this.status.set('won');
    }
  }

  private draw(): void {
    const ctx = this.ctx;
    if (!ctx) {
      return;
    }

    ctx.fillStyle = '#010c05';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    for (const enemy of this.enemies) {
      if (!enemy.alive) {
        continue;
      }
      ctx.fillStyle = enemy.color;
      ctx.save();
      ctx.translate(enemy.x, enemy.y);
      ctx.beginPath();
      ctx.moveTo(0, -ENEMY_HEIGHT / 2);
      ctx.lineTo(ENEMY_WIDTH / 2, ENEMY_HEIGHT / 2);
      ctx.lineTo(-ENEMY_WIDTH / 2, ENEMY_HEIGHT / 2);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    ctx.fillStyle = '#ffb000';
    for (const bullet of this.enemyBullets) {
      ctx.fillRect(bullet.x - 1, bullet.y, 2, 5);
    }

    ctx.fillStyle = '#33ff66';
    for (const bullet of this.playerBullets) {
      ctx.fillRect(bullet.x - 1, bullet.y - 5, 2, 5);
    }

    ctx.fillStyle = '#33ff66';
    ctx.fillRect(this.playerX, PLAYER_Y, PLAYER_WIDTH, PLAYER_HEIGHT);
    ctx.fillRect(this.playerX + PLAYER_WIDTH / 2 - 2, PLAYER_Y - 4, 4, 4);
  }
}
